/**
 * ObjectKeyFacts — the key-facts band atop an object page (graduated from the
 * /styleguide/object-page prototype). A horizontal strip of label-led cells
 * inside the shared StatStrip frame (`Card gap-0 overflow-hidden py-0` +
 * `divide-y/sm:divide-x divide-border/40`), one cell per fact: a muted label
 * over a `type-body` value (with an optional leading intent dot) over an optional
 * muted hint.
 *
 * Distinct from `StatStrip` (whose cells are the value-led `<StatCard>`): these
 * cells are label-led and the first cell typically renders a resolved person
 * (the Owner) with a small avatar — initials for a human, a bot glyph for an
 * agent — so an object's "who owns it + key vitals" reads at a glance without
 * leaking raw ids. Renders nothing when given no facts, so kinds with no vitals
 * collapse the band cleanly. Server-safe (no client hooks); the page resolves
 * names + values, this only presents them.
 */

import * as React from "react";
import { Card } from "../card";
import { IntentDot } from "./status-dot";
import { Bot } from "../icons";
import { cn } from "../utils";
import type { Intent } from "../tokens";

export interface ObjectKeyFact {
  label: string;
  value: React.ReactNode;
  /** One muted line under the value. */
  hint?: string;
  /** Leading intent dot before the value (lifecycle/health signal). */
  intent?: Intent;
  /**
   * When set, the cell renders a resolved person: a small avatar + the name as
   * the value (the Owner cell). `kind` picks the avatar glyph.
   */
  person?: { name: string; kind?: "human" | "agent" };
}

function initials(name: string): string {
  return name
    .replace(/\(.*?\)/g, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

/** Inlined resolved-person marker — initials for humans, a bot glyph for agents. */
function CellAvatar({ name, kind }: { name: string; kind?: "human" | "agent" }) {
  return (
    <span
      aria-hidden
      title={name}
      className={cn(
        "inline-flex size-5 shrink-0 items-center justify-center rounded-full type-subtext",
        kind === "agent"
          ? "bg-primary/10 text-primary"
          : "bg-secondary text-foreground",
      )}
    >
      {kind === "agent" ? (
        <Bot className="size-3" />
      ) : (
        <span className="font-medium leading-none">{initials(name)}</span>
      )}
    </span>
  );
}

export interface ObjectKeyFactsProps {
  facts: ObjectKeyFact[];
  className?: string;
}

export function ObjectKeyFacts({ facts, className }: ObjectKeyFactsProps) {
  if (!facts.length) return null;
  return (
    <Card className={cn("gap-0 overflow-hidden py-0", className)}>
      <div className="flex flex-col divide-y divide-border/40 sm:flex-row sm:divide-x sm:divide-y-0">
        {facts.map((f, i) => (
          <div key={i} className="flex min-w-0 flex-1 flex-col gap-1 px-6 py-4">
            <span className="type-subtext text-muted-foreground">{f.label}</span>
            <span className="flex min-h-5 items-center gap-1.5 type-body font-medium text-foreground">
              {f.person ? (
                <CellAvatar name={f.person.name} kind={f.person.kind} />
              ) : f.intent ? (
                <IntentDot intent={f.intent} size="sm" />
              ) : null}
              <span className={f.person ? "whitespace-nowrap" : undefined}>
                {f.value}
              </span>
            </span>
            {f.hint ? (
              <span className="truncate type-subtext text-muted-foreground">
                {f.hint}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}
